import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { GET_PRODUCT_USAGE } from '@modules/products/products.graphql';
import { LIST_UNIT_OF_MEASURES } from '@modules/settings/settings.graphql';
import {
  GET_TERMINAL_OPERATOR_DASHBOARD,
  GET_TERMINALS_BY_COMPANY,
  SUBMIT_STOCK,
  SUBMIT_TERMINAL_STOCK,
  GET_TERMINAL_STOCK_REPORTS,
  GET_TERMINAL_DAILY_STOCK,
  TERMINAL_DAILY_SUMMAY,
  UPDATE_TERMINAL_STOCK,
  UPDATE_TERMINAL_STOCK_STATUS,
  UPDATE_TERMINAL_TANK,
  GET_CURRENT_DAILY_STOCK_REPORT,
  SUBMIT_TERMINAL_REPORT,
  DELETE_TERMINAL_REQUEST,
  UPDATE_TERMINAL_STOCK_USAGE,
  FIND_ALL_PRODUCT_UNIT_CONVERSIONS,
  DELETE_TANK,
} from '../graphql/terminal-operator-management.graphql';
import { UPDATE_OMC_USAGE_REPORT } from '../../omc-management/graphql/omc-management.graphql';
import * as TerminalOperatorManagementActions from './terminal-operator-management.actions';
import { GetTerminalDailySummary } from '../response/terminal-stock-summary.response';
import {
  UpdateStockStatusResponse,
  UpdateTerminalStockStatusResponse,
} from '../response/stock-status-update.response';
import { UpdateTerminalTankResponse } from '../response/terminal-tank-update.response';
import { GetTerminalDailyStock } from '../response/get-terminal-dailty-stock.response';
import { GetCurrentDailyStock } from '../response/current-daily-stock.response';
import { SubmitTerminalReportResponse } from '../response/submit-terminal-report.response';

@Injectable({
  providedIn: 'root',
})
export class TerminalOperatorManagementEffects {
  private actions$ = inject(Actions);
  private graphQLService = inject(GraphQlService);

  private buildFailure(errorMessage?: string) {
    return {
      success: false,
      statusCode: 400,
      message: errorMessage ?? 'Network error',
      data: null,
    };
  }

  private buildSuccess(data: any, message: string | null = null) {
    return {
      success: true,
      statusCode: 200,
      message,
      data,
    };
  }

  $terminalOperatorDashboard = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.TerminalOperatorDashboardActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<any>({
            query: GET_TERMINAL_OPERATOR_DASHBOARD,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.listMyTerminalOperators;
              if (!payload) {
                return TerminalOperatorManagementActions.TerminalOperatorDashboardActions.failure({
                  error: this.buildFailure(),
                });
              }

              return TerminalOperatorManagementActions.TerminalOperatorDashboardActions.success({
                response: this.buildSuccess(payload),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.TerminalOperatorDashboardActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $terminalsByCompany = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.TerminalsByCompanyActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<any>({
            query: GET_TERMINALS_BY_COMPANY,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              companyUuid: request.companyUuid,
              pageableParam: request.pageableParam ?? null,
            },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getTerminalsByCompany;

              // Supports both API shapes:
              // 1) Wrapped: { status, message, data }
              // 2) Direct pageable: { content, totalPages, ... }
              if (payload?.status === false) {
                return TerminalOperatorManagementActions.TerminalsByCompanyActions.failure({
                  error: this.buildFailure(payload?.message),
                });
              }

              if (!payload) {
                return TerminalOperatorManagementActions.TerminalsByCompanyActions.failure({
                  error: this.buildFailure(),
                });
              }

              return TerminalOperatorManagementActions.TerminalsByCompanyActions.success({
                response: this.buildSuccess(payload?.data ?? payload, payload?.message ?? null),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.TerminalsByCompanyActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $deleteTerminalRequest = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.DeleteTerminalRequestActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .mutate<any>({
            mutation: DELETE_TERMINAL_REQUEST,
            apolloNamespace: ApolloNamespace.registration,
            variables: { terminalUuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.deleteTerminalRequest;
              if (!payload?.status) {
                return TerminalOperatorManagementActions.DeleteTerminalRequestActions.failure({
                  error: this.buildFailure(payload?.message),
                });
              }

              return TerminalOperatorManagementActions.DeleteTerminalRequestActions.success({
                response: this.buildSuccess(payload?.data, payload?.message),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.DeleteTerminalRequestActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $productUsages = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.ProductUsageActions.request),
      switchMap(() =>
        this.graphQLService
          .query<any>({
            query: GET_PRODUCT_USAGE,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getProductUsage;
              if (!payload) {
                return TerminalOperatorManagementActions.ProductUsageActions.failure({
                  error: this.buildFailure(),
                });
              }

              return TerminalOperatorManagementActions.ProductUsageActions.success({
                response: this.buildSuccess(payload?.data ?? [], payload?.message),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.ProductUsageActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $unitOfMeasures = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.UnitOfMeasureActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<any>({
            query: LIST_UNIT_OF_MEASURES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.findAllUnitOfMeasures;
              if (!payload) {
                return TerminalOperatorManagementActions.UnitOfMeasureActions.failure({
                  error: this.buildFailure(),
                });
              }

              return TerminalOperatorManagementActions.UnitOfMeasureActions.success({
                response: this.buildSuccess(payload?.content ?? [], null),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.UnitOfMeasureActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $terminalStockReports = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.TerminalStockReportsActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<any>({
            query: GET_TERMINAL_STOCK_REPORTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getTerminalUsageStock;
              if (!payload) {
                return TerminalOperatorManagementActions.TerminalStockReportsActions.failure({
                  error: this.buildFailure(),
                });
              }

              return TerminalOperatorManagementActions.TerminalStockReportsActions.success({
                response: this.buildSuccess(payload, null),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.TerminalStockReportsActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $terminalDailyStock = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.TerminalDailyStockActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<GetTerminalDailyStock>({
            query: GET_TERMINAL_DAILY_STOCK,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getTerminalDailyStock;
              if (!payload) {
                return TerminalOperatorManagementActions.TerminalDailyStockActions.failure({
                  error: this.buildFailure(),
                });
              }

              return TerminalOperatorManagementActions.TerminalDailyStockActions.success({
                response: this.buildSuccess(payload),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.TerminalDailyStockActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateTerminalStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.TerminalUsageStockUpdateActions.request),
      switchMap(({ request }) => {
        return this.graphQLService
          .mutate<any>({
            mutation: UPDATE_OMC_USAGE_REPORT,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.updateOmcUsage;
              if (!payload?.status) {
                return TerminalOperatorManagementActions.TerminalUsageStockUpdateActions.failure({
                  error: this.buildFailure(payload?.message),
                });
              }
              return TerminalOperatorManagementActions.TerminalUsageStockUpdateActions.success({
                response: this.buildSuccess(payload?.data, payload?.message),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.TerminalUsageStockUpdateActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          );
      }),
    ),
  );

  submitTerminalStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.TerminalStockSubmitActions.request),
      switchMap(({ request }) => {
        return this.graphQLService
          .mutate<any>({
            mutation: SUBMIT_TERMINAL_STOCK,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.submitTerminalStock;
              if (!payload?.status) {
                return TerminalOperatorManagementActions.TerminalStockSubmitActions.failure({
                  error: this.buildFailure(payload?.message),
                });
              }

              return TerminalOperatorManagementActions.TerminalStockSubmitActions.success({
                response: this.buildSuccess(payload?.data, payload?.message),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.TerminalStockSubmitActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          );
      }),
    ),
  );

  $submitTerminalTankStock = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.TerminalTankStockSubmitActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .mutate<any>({
            mutation: SUBMIT_STOCK,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.submitStock;
              if (!payload?.status) {
                return TerminalOperatorManagementActions.TerminalTankStockSubmitActions.failure({
                  error: this.buildFailure(payload?.message),
                });
              }

              return TerminalOperatorManagementActions.TerminalTankStockSubmitActions.success({
                response: this.buildSuccess(payload?.data, payload?.message),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.TerminalTankStockSubmitActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $terminalsDashboardSummary = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.TerminalDashboardSummaryActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<GetTerminalDailySummary>({
            query: TERMINAL_DAILY_SUMMAY,
            apolloNamespace: ApolloNamespace.registration,
            variables: { terminalId: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getTerminalDailySummary;
              if (!payload?.status) {
                return TerminalOperatorManagementActions.TerminalDashboardSummaryActions.failure({
                  error: this.buildFailure(payload?.message),
                });
              }

              return TerminalOperatorManagementActions.TerminalDashboardSummaryActions.success({
                response: this.buildSuccess(payload?.data, payload?.message),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.TerminalDashboardSummaryActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $terminalTankStatusUpdate = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.TerminalTankStatusUpdateActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .mutate<UpdateStockStatusResponse>({
            mutation: UPDATE_TERMINAL_STOCK,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.updateStockStatus;
              if (!payload?.status) {
                return TerminalOperatorManagementActions.TerminalTankStatusUpdateActions.failure({
                  error: this.buildFailure(payload?.message),
                });
              }

              return TerminalOperatorManagementActions.TerminalTankStatusUpdateActions.success({
                response: this.buildSuccess(payload?.data, payload?.message),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.TerminalTankStatusUpdateActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $terminalStockStatusUpdate = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.TerminalStockStatusUpdateActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .mutate<UpdateStockStatusResponse>({
            mutation: UPDATE_TERMINAL_STOCK,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.updateStockStatus;
              if (!payload?.status) {
                return TerminalOperatorManagementActions.TerminalStockStatusUpdateActions.failure({
                  error: this.buildFailure(payload?.message),
                });
              }

              return TerminalOperatorManagementActions.TerminalStockStatusUpdateActions.success({
                response: this.buildSuccess(payload?.data, payload?.message),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.TerminalStockStatusUpdateActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $terminalTankUpdate = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.TerminalTankUpdateActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .mutate<UpdateTerminalTankResponse>({
            mutation: UPDATE_TERMINAL_TANK,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.updateTerminalTank;
              if (!payload?.status) {
                return TerminalOperatorManagementActions.TerminalTankUpdateActions.failure({
                  error: this.buildFailure(payload?.message),
                });
              }

              return TerminalOperatorManagementActions.TerminalTankUpdateActions.success({
                response: this.buildSuccess(payload?.data, payload?.message),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.TerminalTankUpdateActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $currentDailyStock = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.CurrentDailyStockActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<GetCurrentDailyStock>({
            query: GET_CURRENT_DAILY_STOCK_REPORT,
            apolloNamespace: ApolloNamespace.registration,
            variables: { terminalReportId: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data.getCurrentDailyStock;
              if (!payload) {
                return TerminalOperatorManagementActions.CurrentDailyStockActions.failure({
                  error: this.buildFailure(),
                });
              }

              return TerminalOperatorManagementActions.CurrentDailyStockActions.success({
                response: this.buildSuccess(payload),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.CurrentDailyStockActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $submitTerminalReport = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.SubmitTerminalReportActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .mutate<SubmitTerminalReportResponse>({
            mutation: SUBMIT_TERMINAL_REPORT,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.submitTerminalReport;
              if (!payload?.status) {
                return TerminalOperatorManagementActions.SubmitTerminalReportActions.failure({
                  error: this.buildFailure(payload?.message),
                });
              }

              return TerminalOperatorManagementActions.SubmitTerminalReportActions.success({
                response: this.buildSuccess(payload?.data, payload?.message),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.SubmitTerminalReportActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateTerminalStockUsage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.UpdateTerminalStockUsageActions.request),
      switchMap(({ request }) => {
        return this.graphQLService
          .mutate<any>({
            mutation: UPDATE_TERMINAL_STOCK_USAGE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.updateTerminalStockUsage;
              if (!payload?.status) {
                return TerminalOperatorManagementActions.UpdateTerminalStockUsageActions.failure({
                  error: this.buildFailure(payload?.message),
                });
              }
              return TerminalOperatorManagementActions.UpdateTerminalStockUsageActions.success({
                response: this.buildSuccess(payload?.data, payload?.message),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.UpdateTerminalStockUsageActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          );
      }),
    ),
  );


  findAllProductUnitConversions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.FindAllProductUnitConversionsActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<any>({
            query: FIND_ALL_PRODUCT_UNIT_CONVERSIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.findAllProductUnitConversions;
              if (!payload) {
                throw new Error('Empty dataset returned from registry.');
              }
              return TerminalOperatorManagementActions.FindAllProductUnitConversionsActions.success({
                response: this.buildSuccess(payload),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.FindAllProductUnitConversionsActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $deleteTank = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorManagementActions.DeleteTankActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .mutate<any>({
            mutation: DELETE_TANK,
            apolloNamespace: ApolloNamespace.registration,
            variables: { tankUuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.deleteTank;
              if (!payload) {
                return TerminalOperatorManagementActions.DeleteTankActions.failure({
                  error: this.buildFailure('Failed to delete tank.'),
                });
              }

              if (payload.status === 'FAILED' || payload.code === '400' || payload.code === 400) {
                return TerminalOperatorManagementActions.DeleteTankActions.failure({
                  error: this.buildFailure(payload.message || 'Failed to delete tank.'),
                });
              }

              return TerminalOperatorManagementActions.DeleteTankActions.success({
                response: this.buildSuccess(payload, payload.message || 'Tank deleted successfully.'),
              });
            }),
            catchError((err) =>
              of(
                TerminalOperatorManagementActions.DeleteTankActions.failure({
                  error: this.buildFailure(err?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );
}